/**
 * useEnrichmentQueueDetector — mounted once at AppLayout level.
 *
 * Walks the enrichment-queue store, polls /contacts/enrichment-status/{id}
 * for each entry still in `processing`, and on terminal transitions:
 *   - mutates the store with the final status + hasEmail/hasPhone flags
 *   - fires an Ant Design top-right toast
 *   - fires a Browser desktop Notification (opt-in: requests permission on
 *     the first completion of a session, never prompts again that session
 *     if denied)
 *   - marks `alertEmitted` to dedupe
 *
 * Polling is done with a single shared interval (3s) instead of one
 * React Query instance per entry — that keeps tab count bounded and lets
 * us bail entirely when the queue is empty.
 */

import { useEffect, useRef } from 'react';
import { message as antMessage } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import {
  pertenceAoEscopoDoEnriquecimento,
  useEnrichmentQueueEntriesForCurrentOwner,
  useEnrichmentQueueStore,
  type EnrichmentQueueEntry,
} from '@/shared/stores/useEnrichmentQueueStore';
import { contactsApi } from '@/modules/pipeline/services/contactsApi';
import { prospectsApi } from '@/modules/prospects/services/prospectsApi';
import { intelligenceApi } from '@/modules/pipeline/services/intelligenceApi';

const POLL_INTERVAL_MS = 3_000;
/** Polls past this age fail-open (status="processing") so we don't
 *  hammer the BFF for tasks the user never reloaded into. The store
 *  GCs them on next mount via clearOlderThan. */
const MAX_POLL_AGE_MS = 5 * 60 * 1000;

/** Grace period before flipping an unlock that returned EMPTY data to
 *  `no_data`. Lusha sometimes returns success=true with empty contacts
 *  on the first provider response and populates the actual email/phone
 *  on a subsequent retry (Apify fallback in enrichment-proxy). UX
 *  requirement: hold the "AI Enriching" badge for up to 60s, with a
 *  mid-point invalidation of contact queries at 30s to pull fresh DB
 *  state, before declaring "Sem dados disponíveis". */
const NO_DATA_GRACE_MS = 60_000;
const NO_DATA_REFRESH_AT_MS = 30_000;

/** Grace period before declaring a `contact_unlock` terminally failed.
 *
 *  The enrichment subscriber writes Redis `status="failed"` as soon as the
 *  FIRST provider response (e.g. Lusha) comes back unsuccessful, even when a
 *  parallel/fallback provider (Apify) may still be in-flight. When the second
 *  provider succeeds, the subscriber promotes Redis to `"completed"` AND
 *  writes email/phone to the contact in DB — but if we declared `failed`
 *  terminally on the first poll, the queue/toast stay stuck on "Falha" while
 *  the contact table already shows the data. This window gives the fallback
 *  provider time to land before we surface a failure to the user. */
const FAILED_GRACE_MS = 60_000;

/** Phone search (per-contact "Buscar telefone a fundo" → Apollo waterfall) can
 *  take MINUTES, far longer than the seconds-scale Lusha/Apify reveal. So for
 *  `phone_search` we keep polling and NEVER declare "telefone não encontrado"
 *  before this window — the detector cross-checks the DB reveal-status every
 *  tick, so the moment the async phone lands it flips straight to success. */
const PHONE_SEARCH_NO_DATA_AT_MS = 5 * 60 * 1000;
/** Poll-age ceiling for phone_search — a bit past the no-data threshold so the
 *  terminal toast fires before the entry is GC'd. */
const PHONE_SEARCH_MAX_POLL_MS = 6 * 60 * 1000;

const NOTIFICATION_ICON = '/janusos-icon.svg';

/**
 * Request notification permission once per session, lazily — at the
 * moment a completion would surface. We do NOT prompt eagerly on mount
 * because the gesture is unsolicited and most users click "Block".
 */
function ensureNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return Promise.resolve('denied');
  }
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Promise.resolve(Notification.permission);
  }
  try {
    return Notification.requestPermission();
  } catch {
    return Promise.resolve('denied');
  }
}

function fireDesktopNotification(entry: EnrichmentQueueEntry): void {
  try {
    if (entry.type === 'news_monitor') {
      const title =
        entry.status === 'completed'
          ? 'Notícias prontas'
          : 'Não foi possível buscar as notícias.';
      const body =
        entry.status === 'completed'
          ? 'As notícias da empresa estão prontas.'
          : entry.label;
      const n = new Notification(title, { body, icon: NOTIFICATION_ICON });
      void n;
      return;
    }
    if (entry.type === 'linkedin_posts') {
      const ok = entry.status === 'completed';
      const title = ok
        ? 'Postagens do LinkedIn prontas'
        : entry.status === 'no_data'
          ? 'Sem postagens recentes'
          : 'Não foi possível buscar as postagens.';
      const body = ok ? entry.label : `${entry.label} — ${
        entry.status === 'no_data' ? 'sem postagens públicas' : 'falha na busca'
      }`;
      const n = new Notification(title, { body, icon: NOTIFICATION_ICON });
      void n;
      return;
    }
    if (entry.type === 'linkedin_experience') {
      const ok = entry.status === 'completed';
      const title = ok
        ? 'Perfil consolidado pronto'
        : entry.status === 'no_data'
          ? 'Perfil não encontrado'
          : 'Não foi possível consolidar o perfil.';
      const body = ok ? entry.label : `${entry.label} — ${
        entry.status === 'no_data' ? 'sem perfil público' : 'falha na consolidação'
      }`;
      const n = new Notification(title, { body, icon: NOTIFICATION_ICON });
      void n;
      return;
    }
    if (entry.type === 'phone_search') {
      const ok = entry.status === 'completed';
      const title = entry.outcome === 'phone_wait_timeout'
        ? 'Telefone ainda não retornou'
        : ok
        ? 'Telefone encontrado'
        : entry.status === 'no_data'
          ? 'Telefone não encontrado'
          : 'Falha na busca de telefone';
      const n = new Notification(title, { body: entry.label, icon: NOTIFICATION_ICON });
      void n;
      return;
    }
    const isLead = entry.type === 'lead_creation';
    const title =
      entry.status === 'completed'
        ? (isLead ? 'Lead enriquecido' : 'Contato pronto')
        : 'Enrichment finalizado';
    const body =
      entry.status === 'completed'
        ? entry.label
        : `${entry.label} — sem dados disponíveis`;
    const n = new Notification(title, { body, icon: NOTIFICATION_ICON });
    void n;
  } catch {
    /* swallow — desktop notification is best-effort */
  }
}

function fireToast(entry: EnrichmentQueueEntry): void {
  if (entry.type === 'news_monitor') {
    if (entry.status === 'completed') {
      antMessage.success({ content: 'Notícias prontas', duration: 4 });
    } else {
      antMessage.error({
        content: 'Não foi possível buscar as notícias.',
        duration: 4,
      });
    }
    return;
  }
  if (entry.type === 'linkedin_posts') {
    if (entry.status === 'completed') {
      antMessage.success({
        content: `Postagens do LinkedIn prontas — ${entry.label}`,
        duration: 4,
      });
    } else if (entry.status === 'no_data') {
      antMessage.info({
        content: `${entry.label} — sem postagens públicas recentes`,
        duration: 4,
      });
    } else {
      antMessage.error({
        content: `${entry.label} — falha ao buscar postagens`,
        duration: 4,
      });
    }
    return;
  }
  if (entry.type === 'linkedin_experience') {
    if (entry.status === 'completed') {
      antMessage.success({
        content: `Perfil consolidado pronto — ${entry.label}`,
        duration: 4,
      });
    } else if (entry.status === 'no_data') {
      antMessage.info({
        content: `${entry.label} — nenhum perfil público encontrado`,
        duration: 4,
      });
    } else {
      antMessage.error({
        content: `${entry.label} — falha ao consolidar o perfil`,
        duration: 4,
      });
    }
    return;
  }
  if (entry.type === 'phone_search') {
    if (entry.status === 'completed') {
      antMessage.success({ content: `Telefone encontrado — ${entry.label}`, duration: 5 });
    } else if (entry.outcome === 'phone_wait_timeout') {
      antMessage.info({ content: `${entry.label} — o telefone ainda não retornou. Consulte o contato mais tarde; nenhuma nova busca foi iniciada.`, duration: 8 });
    } else if (entry.status === 'no_data') {
      antMessage.info({ content: `${entry.label} — telefone não encontrado`, duration: 5 });
    } else {
      antMessage.error({ content: `${entry.label} — falha na busca de telefone`, duration: 5 });
    }
    return;
  }
  const isLead = entry.type === 'lead_creation';
  if (entry.status === 'completed') {
    antMessage.success({
      content: isLead ? `${entry.label} enriquecido` : `${entry.label} revelado`,
      duration: 4,
    });
  } else if (entry.status === 'failed') {
    antMessage.error({
      content: `${entry.label} — falha no enriquecimento`,
      duration: 4,
    });
  } else if (entry.status === 'no_data') {
    antMessage.info({
      content: `${entry.label} — sem dados disponíveis`,
      duration: 4,
    });
  }
}

/**
 * Decide the terminal status from the backend payload. The backend's
 * `result` for a contact_unlock carries the FULL provider response —
 * historically:
 *
 *   { success: bool,
 *     contacts: [{ contactId, fullName, email, telefone, linkedinUrl, ... }],
 *     creditsCharged: N }
 *
 * Earlier this function read `result.email`/`result.telefone` from the
 * TOP level, which is always undefined → we incorrectly mapped every
 * successful reveal to `no_data`. The user saw "Sem dados disponíveis"
 * in the queue popover even though the contact had email+phone in DB.
 *
 * Now we look inside `contacts[0]` first (canonical shape) and fall
 * back to top-level fields for safety.
 *
 * Empty email AND phone → `no_data` (caller may render a different
 * affordance — "Lusha não tinha dados" vs "reveal pronto, abra").
 */
function deriveTerminal(
  rawStatus: string,
  result: unknown,
  startedAt: number,
): { status: 'completed' | 'failed' | 'no_data'; hasEmail: boolean; hasPhone: boolean; outcome?: string } | null {
  if (rawStatus === 'failed') {
    return { status: 'failed', hasEmail: false, hasPhone: false };
  }
  if (rawStatus !== 'completed') {
    return null;
  }
  const r = result as Record<string, unknown> | null;
  const contacts = r && Array.isArray(r.contacts) ? r.contacts : [];
  const first = (contacts[0] as { email?: string; telefone?: string } | undefined) ?? null;
  const email =
    (first?.email && String(first.email).trim()) ||
    (r as { email?: string } | null)?.email ||
    '';
  const telefone =
    (first?.telefone && String(first.telefone).trim()) ||
    (r as { telefone?: string } | null)?.telefone ||
    '';
  const hasEmail = !!(email && String(email).trim());
  const hasPhone = !!(telefone && String(telefone).trim());
  if (hasEmail || hasPhone) {
    return { status: 'completed', hasEmail, hasPhone };
  }
  // Empty data: apply grace period. The first provider response often
  // arrives within seconds but lacks the phone (Lusha v3 vs Apify
  // race); the subscriber writes whatever arrives, but the second
  // provider may populate data shortly after. Returning `null` here
  // keeps the entry in `processing` state on the next tick.
  const elapsed = Date.now() - startedAt;
  if (elapsed < NO_DATA_GRACE_MS) {
    return null;
  }
  return { status: 'no_data', hasEmail: false, hasPhone: false };
}

export function useEnrichmentQueueDetector(): void {
  const entries = useEnrichmentQueueEntriesForCurrentOwner();
  const markStatus = useEnrichmentQueueStore((s) => s.markStatus);
  const markAlertEmitted = useEnrichmentQueueStore((s) => s.markAlertEmitted);
  const clearOlderThan = useEnrichmentQueueStore((s) => s.clearOlderThan);
  const qc = useQueryClient();

  // Track which messageIds are mid-poll so we don't pile up concurrent
  // requests if the interval fires before the previous batch resolved.
  const inflightRef = useRef<Set<string>>(new Set());
  // Track which entries already triggered the 30s mid-point refresh so
  // we don't spam invalidateQueries on every tick after the threshold.
  const refreshedAtRef = useRef<Set<string>>(new Set());

  // GC pass once on mount — drops anything older than 10 min.
  useEffect(() => {
    clearOlderThan(10 * 60 * 1000);
  }, [clearOlderThan]);

  // Refresh the contact list queries an entry feeds. When the enqueuing
  // caller recorded the company CNPJ we can invalidate the exact
  // `by-cnpj` / `by-deal` keys DealContacts uses; otherwise fall back to
  // the broad `['contacts']` prefix so no mounted list is left stale.
  const invalidateContactLists = (entry: EnrichmentQueueEntry) => {
    const cnpj = (entry.cnpj ?? '').replace(/\D/g, '');
    if (cnpj) {
      qc.invalidateQueries({ queryKey: ['contacts', 'by-cnpj', cnpj] });
      if (entry.dealId) {
        qc.invalidateQueries({ queryKey: ['contacts', 'by-deal', entry.dealId] });
      }
    } else {
      qc.invalidateQueries({ queryKey: ['contacts'], exact: false });
    }
    qc.invalidateQueries({ queryKey: ['contact-unlocks'], exact: false });
  };

  useEffect(() => {
    // Reabertura depois do prazo: parar acompanhamento, sem afirmar ausência.
    entries.filter(e => pertenceAoEscopoDoEnriquecimento(e) && e.type === 'phone_search' && e.status === 'processing'
      && Date.now() - e.startedAt >= PHONE_SEARCH_MAX_POLL_MS)
      .forEach(e => markStatus(e.messageId, 'no_data', { outcome: 'phone_wait_timeout' }));
    const processing = entries.filter(
      (e) =>
        e.status === 'processing' &&
        Date.now() - e.startedAt <
          (e.type === 'phone_search' ? PHONE_SEARCH_MAX_POLL_MS : MAX_POLL_AGE_MS),
    );

    if (processing.length === 0) return;

    const tick = async () => {
      useEnrichmentQueueStore.getState().entries
        .filter(e => pertenceAoEscopoDoEnriquecimento(e) && e.type === 'phone_search'
          && e.status === 'processing' && Date.now() - e.startedAt >= PHONE_SEARCH_MAX_POLL_MS)
        .forEach(e => markStatus(e.messageId, 'no_data', { outcome: 'phone_wait_timeout' }));
      // Snapshot current processing list; useState updates between ticks
      // are picked up by the next effect run.
      const current = useEnrichmentQueueStore
        .getState()
        .entries.filter(
          (e) =>
            pertenceAoEscopoDoEnriquecimento(e) &&
            e.status === 'processing' &&
            Date.now() - e.startedAt < (e.type === 'phone_search' ? PHONE_SEARCH_MAX_POLL_MS : MAX_POLL_AGE_MS),
        );

      await Promise.all(
        current.map(async (entry) => {
          if (inflightRef.current.has(entry.messageId)) return;
          inflightRef.current.add(entry.messageId);
          // Mid-point refresh: at 30s elapsed, invalidate the contacts
          // queries so the cell-level UI re-pulls fresh DB state. The
          // enrichment subscriber may have written email/phone to
          // contact_core after our initial poll saw empty results.
          const elapsedAtTick = Date.now() - entry.startedAt;
          if (
            (entry.type === 'contact_unlock' || entry.type === 'phone_search') &&
            elapsedAtTick >= NO_DATA_REFRESH_AT_MS &&
            !refreshedAtRef.current.has(entry.messageId)
          ) {
            refreshedAtRef.current.add(entry.messageId);
            invalidateContactLists(entry);
          }
          try {
            // Route to the right polling endpoint by entry type:
            //   contact_unlock → /contacts/enrichment-status/{messageId}
            //   lead_creation  → /prospects/{prospectId} (check enrichmentReady)
            //   news_monitor   → /research/enrichment-status/{messageId}
            let terminal: ReturnType<typeof deriveTerminal>;
            if (entry.type === 'lead_creation') {
              const p = await prospectsApi.getById(entry.messageId);
              if (!p?.enrichmentReady) {
                return;
              }
              terminal = { status: 'completed', hasEmail: false, hasPhone: false };
            } else if (entry.type === 'news_monitor') {
              const res = await intelligenceApi.getNewsEnrichmentStatus(entry.messageId);
              if (res.status === 'completed') {
                terminal = { status: 'completed', hasEmail: false, hasPhone: false };
              } else if (res.status === 'failed') {
                terminal = { status: 'failed', hasEmail: false, hasPhone: false };
              } else if (res.status === 'no_data') {
                terminal = { status: 'no_data', hasEmail: false, hasPhone: false };
              } else {
                // 'processing' / 'not_found' → keep polling.
                return;
              }
            } else if (entry.type === 'phone_search') {
              // Per-contact deep phone search (Apollo waterfall). The phone
              // arrives async — possibly minutes later. Treat it as done the
              // moment a phone is present, either in the enrichment-status
              // result OR (more authoritative) in the DB reveal-status. We
              // NEVER declare "telefone não encontrado" before
              // PHONE_SEARCH_NO_DATA_AT_MS — that was the old false-negative.
              const res = await contactsApi.getEnrichmentStatus(entry.messageId);
              const r = res.result as { contacts?: Array<{ telefone?: string }> } | null;
              let phoneFound = !!(
                r?.contacts?.[0]?.telefone && String(r.contacts[0].telefone).trim()
              );
              if (!phoneFound && entry.contactCoreId) {
                try {
                  const reveal = await contactsApi.getRevealStatus(entry.contactCoreId);
                  phoneFound = !!reveal.has_phone;
                } catch {
                  /* reveal-status unreachable — fall through to the grace window */
                }
              }
              if (phoneFound) {
                terminal = { status: 'completed', hasEmail: false, hasPhone: true };
              } else if (Date.now() - entry.startedAt >= PHONE_SEARCH_NO_DATA_AT_MS) {
                terminal = { status: 'no_data', hasEmail: false, hasPhone: false, outcome: 'phone_wait_timeout' };
              } else {
                // Still searching — keep polling; never a premature "no phone".
                return;
              }
            } else if (
              entry.type === 'linkedin_posts' ||
              entry.type === 'linkedin_experience'
            ) {
              // Both LinkedIn enrichments share the generic contact
              // enrichment-status endpoint. They have no email/phone outcome
              // — completed / no_data / failed are all terminal.
              const res = await contactsApi.getEnrichmentStatus(entry.messageId);
              if (res.status === 'completed') {
                terminal = { status: 'completed', hasEmail: false, hasPhone: false };
              } else if (res.status === 'failed') {
                terminal = { status: 'failed', hasEmail: false, hasPhone: false };
              } else if (res.status === 'no_data') {
                terminal = { status: 'no_data', hasEmail: false, hasPhone: false };
              } else {
                // 'pending' / 'processing' / 'not_found' → keep polling.
                return;
              }
            } else {
              const res = await contactsApi.getEnrichmentStatus(entry.messageId);
              terminal = deriveTerminal(res.status, res.result, entry.startedAt);
              if (!terminal) return;

              // Race recovery: the subscriber writes Redis status="failed"
              // the moment the first provider (Lusha) fails, but a parallel
              // or fallback provider (Apify) may still succeed seconds later
              // and write data to DB. Before declaring terminal failure,
              // cross-check the DB-backed reveal-status: if the contact ALREADY
              // has email/phone or the link was settled to complete/partial,
              // surface success. Otherwise hold the entry in `processing`
              // until FAILED_GRACE_MS elapses, then accept the failure.
              if (
                terminal.status === 'failed' &&
                entry.type === 'contact_unlock' &&
                entry.contactCoreId
              ) {
                try {
                  const reveal = await contactsApi.getRevealStatus(entry.contactCoreId);
                  const settledOk =
                    reveal.has_email ||
                    reveal.has_phone ||
                    reveal.billing_status === 'complete' ||
                    reveal.billing_status === 'partial_email' ||
                    reveal.billing_status === 'partial_phone';
                  if (settledOk) {
                    terminal = {
                      status: 'completed',
                      hasEmail: !!reveal.has_email,
                      hasPhone: !!reveal.has_phone,
                    };
                  } else if (Date.now() - entry.startedAt < FAILED_GRACE_MS) {
                    // Fallback provider may still land — keep polling.
                    return;
                  }
                } catch {
                  // reveal-status unreachable: fall through to terminal=failed.
                }
              }
            }
            // A conta/tenant pode mudar enquanto a chamada ao BFF aguarda.
            // Não grave estado, invalide queries nem notifique sob o novo token.
            if (!pertenceAoEscopoDoEnriquecimento(entry)) return;
            markStatus(entry.messageId, terminal.status, {
              hasEmail: terminal.hasEmail,
              hasPhone: terminal.hasPhone,
              outcome: terminal.outcome,
            });

            // Final invalidation on terminal so the cells flip to the
            // final state (data or no_data UI) immediately, without
            // waiting for React Query's staleTime.
            if (entry.type === 'contact_unlock' || entry.type === 'phone_search') {
              invalidateContactLists(entry);
            }

            // News monitor: surface the freshly-fetched news in MeetingNewsTab.
            // The tab polls ['news-enrichment', messageId] directly, but also
            // invalidate its legacy sync key ['meeting-news', companyName] so a
            // mounted tab re-pulls. The label is "<companyName> — Notícias".
            if (entry.type === 'news_monitor' && terminal.status === 'completed') {
              qc.invalidateQueries({
                queryKey: ['news-enrichment', entry.messageId],
                exact: false,
              });
              const companyName = entry.label.replace(/\s+—\s+Notícias$/, '');
              qc.invalidateQueries({
                queryKey: ['meeting-news', companyName],
                exact: false,
              });
            }

            // LinkedIn posts: ContactDetailDrawer polls the result keyed by
            // messageId (['linkedin-posts', messageId]). Invalidate it on the
            // terminal tick so the panel flips Skeleton → feed immediately;
            // also invalidate the per-contact key as a belt-and-suspenders.
            if (entry.type === 'linkedin_posts') {
              qc.invalidateQueries({
                queryKey: ['linkedin-posts', entry.messageId],
                exact: false,
              });
              if (entry.contactCoreId) {
                qc.invalidateQueries({
                  queryKey: ['linkedin-posts', entry.contactCoreId],
                  exact: false,
                });
              }
            }

            // LinkedIn experience / profile consolidation: same pattern,
            // key ['contact-experience', messageId].
            if (entry.type === 'linkedin_experience') {
              qc.invalidateQueries({
                queryKey: ['contact-experience', entry.messageId],
                exact: false,
              });
              if (entry.contactCoreId) {
                qc.invalidateQueries({
                  queryKey: ['contact-experience', entry.contactCoreId],
                  exact: false,
                });
              }
            }

            // Alert flow — guarded against double-fire by alertEmitted flag.
            const latest = useEnrichmentQueueStore
              .getState()
              .entries.find(
                (e) =>
                  e.messageId === entry.messageId && pertenceAoEscopoDoEnriquecimento(e),
              );
            if (!latest || latest.alertEmitted) return;
            fireToast({ ...entry, status: terminal.status, outcome: terminal.outcome });
            const perm = await ensureNotificationPermission();
            if (perm === 'granted') {
              fireDesktopNotification({ ...entry, status: terminal.status, outcome: terminal.outcome });
            }
            markAlertEmitted(entry.messageId);
          } catch {
            // Network or 404 — leave it processing; next poll will retry.
            // After MAX_POLL_AGE_MS the entry is filtered out and GC'd.
          } finally {
            inflightRef.current.delete(entry.messageId);
          }
        }),
      );
    };

    // Fire one tick immediately so the first completion is visible quickly
    // after mount; then settle into the regular cadence.
    void tick();
    const handle = window.setInterval(tick, POLL_INTERVAL_MS);
    // Background tabs get their timers throttled by the browser (Chrome
    // clamps to ~1/min after 5 min hidden), so the observed cadence can
    // degrade to 40s+. Fire a tick the moment the tab becomes visible so
    // the user never stares at a stale "processando…" that finished ages
    // ago while the tab was hidden.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(handle);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // We depend on `entries.length` to (re)mount the interval when the
    // queue size changes — re-running on every entry mutation would
    // thrash the interval. The tick function reads the freshest state
    // via getState() so newly enqueued entries are picked up on the
    // next tick anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries.length, markStatus, markAlertEmitted]);
}
